import React from 'react'
import { ShoppingCart, DollarSign, Store, Users } from 'lucide-react'
import StatCard from '../../components/admin/StatCard'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

// Demo data
const salesData = [
  { name: "Jan", sales: 4000 },
  { name: "Feb", sales: 3000 },
  { name: "Mar", sales: 5000 },
  { name: "Apr", sales: 4500 },
  { name: "May", sales: 6000 },
  { name: "Jun", sales: 5500 },
  { name: "Jul", sales: 7000 },
]

const ordersData = [
  { name: "Mon", orders: 24 },
  { name: "Tue", orders: 13 },
  { name: "Wed", orders: 32 },
  { name: "Thu", orders: 27 },
  { name: "Fri", orders: 38 },
  { name: "Sat", orders: 45 },
  { name: "Sun", orders: 29 },
]

const recentOrders = [
  { id: "#ORD-001", customer: "Rahul Sharma", product: "Money Plant", amount: 450, status: "Delivered" },
  { id: "#ORD-002", customer: "Priya Patel", product: "Snake Plant", amount: 680, status: "Shipped" },
  { id: "#ORD-003", customer: "Amit Kumar", product: "Peace Lily", amount: 520, status: "Pending" },
  { id: "#ORD-004", customer: "Sneha Gupta", product: "Fiddle Leaf Fig", amount: 1200, status: "Delivered" },
  { id: "#ORD-005", customer: "Vikram Singh", product: "Rubber Plant", amount: 380, status: "Cancelled" },
]

const pendingSellers = [
  { id: 1, name: "Green Gardens Nursery", email: "contact@greengardens.com", date: "2024-01-15" },
  { id: 2, name: "Plant Paradise", email: "info@plantparadise.com", date: "2024-01-14" },
  { id: 3, name: "Nature's Touch", email: "hello@naturestouch.com", date: "2024-01-13" },
]

const getStatusBadge = (status) => {
  const styles = {
    Delivered: "bg-success",
    Shipped: "bg-info",
    Pending: "bg-warning",
    Cancelled: "bg-danger"
  }
  return (
    <span className={`badge ${styles[status] || 'bg-secondary'} text-white px-2 py-1`}>
      {status}
    </span>
  )
}

const AdminDashboard = () => {
  return (
    <div>
      {/* Page Header */}
      <div className="mb-4">
        <h1 className="h2 fw-bold mb-1">Dashboard</h1>
        <p className="text-muted">Welcome back! Here's what's happening with your store.</p>
      </div>

      {/* Stats Grid */}
      <div className="row g-4 mb-4">
        <div className="col-sm-6 col-lg-3">
          <StatCard
            title="Total Orders"
            value="1,284"
            change="+12.5% from last month"
            changeType="positive"
            icon={ShoppingCart}
            iconColor="bg-primary"
          />
        </div>
        <div className="col-sm-6 col-lg-3">
          <StatCard
            title="Total Revenue"
            value="₹4,58,320"
            change="+8.2% from last month"
            changeType="positive"
            icon={DollarSign}
            iconColor="bg-success"
          />
        </div>
        <div className="col-sm-6 col-lg-3">
          <StatCard
            title="Total Sellers"
            value="156"
            change="+5 new this month"
            changeType="positive"
            icon={Store}
            iconColor="bg-warning"
          />
        </div>
        <div className="col-sm-6 col-lg-3">
          <StatCard
            title="Total Customers"
            value="3,842"
            change="+124 new this month"
            changeType="positive"
            icon={Users}
            iconColor="bg-info"
          />
        </div>
      </div>

      {/* Charts */}
      <div className="row g-4 mb-4">
        {/* Sales Chart */}
        <div className="col-lg-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title fw-semibold mb-3">Sales Overview</h5>
              <div style={{ height: '320px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={salesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="sales" stroke="#22c55e" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Chart */}
        <div className="col-lg-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title fw-semibold mb-3">Weekly Orders</h5>
              <div style={{ height: '320px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ordersData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="orders" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tables */}
      <div className="row g-4">
        {/* Recent Orders */}
        <div className="col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-white border-bottom">
              <h5 className="card-title fw-semibold mb-0">Recent Orders</h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="small text-muted">Order ID</th>
                      <th className="small text-muted">Customer</th>
                      <th className="small text-muted">Amount</th>
                      <th className="small text-muted">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((order) => (
                      <tr key={order.id}>
                        <td className="fw-medium">{order.id}</td>
                        <td>{order.customer}</td>
                        <td>₹{order.amount}</td>
                        <td>{getStatusBadge(order.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Seller Approvals */}
        <div className="col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-white border-bottom">
              <h5 className="card-title fw-semibold mb-0">Pending Seller Approvals</h5>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                {pendingSellers.map((seller) => (
                  <div key={seller.id} className="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 fw-semibold">{seller.name}</h6>
                      <small className="text-muted">{seller.email}</small>
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-success btn-sm">
                        <i className="bi bi-check-lg"></i> Approve
                      </button>
                      <button className="btn btn-outline-secondary btn-sm">
                        Review
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard